import { Box, em, Stack } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronDown } from "@tabler/icons-react";
import React, { ReactNode, useEffect, useState } from "react";

import { useBreakpoint } from "@contexts";

import { Card, CardProps, Text } from "@components";

import * as classes from "./Hero.module.css";

type ResponsiveBooleanValue =
  | boolean
  | {
      base?: boolean;
      xs?: boolean;
      sm?: boolean;
      md?: boolean;
      lg?: boolean;
      xl?: boolean;
    };

export interface HeroProps extends CardProps {
  backgroundImage: ReactNode;
  children?: ReactNode;
  scrollIndicator?: ResponsiveBooleanValue;
  height?: CardProps["h"];
  padding?: string;
  className?: string;
}

// Utility function to resolve responsive boolean values
function useResponsiveBooleanValue(value: ResponsiveBooleanValue): boolean {
  const { isXs, isSm, isMd, isLg, isXl } = useBreakpoint();

  if (typeof value === "boolean") {
    return value;
  }

  // Find the appropriate value based on current breakpoint
  if (isXl && value.xl !== undefined) return value.xl;
  if (isLg && value.lg !== undefined) return value.lg;
  if (isMd && value.md !== undefined) return value.md;
  if (isSm && value.sm !== undefined) return value.sm;
  if (isXs && value.xs !== undefined) return value.xs;

  // Fallback to base or false if no matching breakpoint
  return value.base ?? false;
}

export function Hero({
  backgroundImage,
  children,
  scrollIndicator = false,
  height = "calc(100dvh - 40px)",
  padding,
  className,
  ...cardProps
}: HeroProps) {
  const isMobile = useMediaQuery(`(max-width: ${em(992)})`);
  const [scrollY, setScrollY] = useState(0);

  // Resolve responsive scrollIndicator value
  const resolvedScrollIndicator = useResponsiveBooleanValue(scrollIndicator);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Set initial value

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollClick = () => {
    window.scrollTo({
      top: window.innerHeight - (isMobile ? 102 : 147),
      behavior: "smooth",
    });
  };

  // Calculate opacity and transform based on scroll
  const indicatorOpacity = Math.max(0, 1 - scrollY / 200);
  const indicatorTransform = Math.min(scrollY / 4, 50);

  return (
    <>
      <Card
        className={`${classes.card} ${className || ""}`}
        bg="layoutDark"
        px={padding}
        backgroundImage={backgroundImage}
        pos="relative"
        {...cardProps}
      >
        <Stack
          className={classes.innerStack}
          mx="auto"
          mih={{ base: 0, md: 1000 }}
          h={height}
          align="center"
          justify="center"
        >
          {children}
        </Stack>

        {resolvedScrollIndicator && (
          <Box
            className={classes.scrollIndicator}
            onClick={handleScrollClick}
            style={{
              opacity: indicatorOpacity * 0.6,
              transform: `translateX(-50%) translateY(-${indicatorTransform}px)`,
              pointerEvents: indicatorOpacity > 0.1 ? "auto" : "none",
            }}
          >
            <Text size="xs" c="fontDarkSecondary" mb="xs">
              Scroll for more
            </Text>
            <IconChevronDown size={24} className={classes.scrollArrow} />
          </Box>
        )}
      </Card>
    </>
  );
}
